import {
	DEFAULT_THRESHOLD_TIER,
	RECOMMENDED_THRESHOLDS,
	RELEVANCE_CURVES,
} from "./constants";

export {
	DEFAULT_CATEGORY_QUERY_THRESHOLD,
	DEFAULT_DOMAIN_EXCLUDE_QUERY_THRESHOLD,
	DEFAULT_DOMAIN_INCLUDE_QUERY_THRESHOLD,
	DEFAULT_KEYWORD_QUERY_THRESHOLD,
	DEFAULT_QUERY_SCORE,
	DEFAULT_THRESHOLD_TIER,
	RECOMMENDED_THRESHOLDS,
	RELEVANCE_CURVE_EXPONENT,
	RELEVANCE_CURVES,
	RELEVANCE_SCORE_MIDPOINT,
} from "./constants";

const getCurve = (api) => RELEVANCE_CURVES[api] || RELEVANCE_CURVES.DOMAINS;

/**
 * Cosine distance -> relevance percentage, using the API's sigmoid curve.
 * Returns an unrounded 0-100 value.
 */
export const relevanceFromDistance = (distance, api) => {
	const numeric = Number(distance);
	if (!Number.isFinite(numeric)) return 0;
	if (numeric <= 0) return 100;

	const { denominator, exponent } = getCurve(api);
	const relevance = 100 / (1 + (numeric / denominator) ** exponent);
	return Math.max(0, Math.min(100, relevance));
};

/**
 * Relevance percentage (0-100) -> cosine distance. Inverse of the sigmoid.
 * Returns null when the relevance is outside the representable range.
 */
export const distanceFromRelevance = (relevance, api) => {
	const numeric = Number(relevance);
	if (!Number.isFinite(numeric) || numeric <= 0 || numeric > 100) return null;
	if (numeric === 100) return 0;

	const { denominator, exponent } = getCurve(api);
	return denominator * (100 / numeric - 1) ** (1 / exponent);
};

/** Recommended threshold for an API/tier, falling back to the default tier. */
export const getRecommendedThreshold = (api, tier = DEFAULT_THRESHOLD_TIER) => {
	const tiers = RECOMMENDED_THRESHOLDS[api] || {};
	return tiers[tier] ?? tiers[DEFAULT_THRESHOLD_TIER] ?? null;
};

/**
 * Resolve the cosine distance to send to the API for a search term.
 *
 * Terms carry a `threshold` (already a cosine distance, 0-1) once a tier has
 * been picked, and a `score` (relevance %, 1-100) for display. Prefer the exact
 * distance; only fall back to inverting the curve from the displayed score.
 */
export const resolveQueryThreshold = (
	term = {},
	api,
	fallbackTier = DEFAULT_THRESHOLD_TIER,
) => {
	const explicit = Number(term.threshold);
	if (Number.isFinite(explicit) && explicit >= 0 && explicit <= 1) {
		return explicit;
	}

	const score = Number(term.score ?? term.originalScore);
	if (Number.isFinite(score) && score > 1 && score <= 100) {
		const converted = distanceFromRelevance(score, api);
		if (converted !== null) return converted;
	}

	return getRecommendedThreshold(api, fallbackTier) ?? 0;
};

/**
 * Build the tier dropdown options for a search interface. Each entry keeps the
 * caller's wording and gains the sheet's cosine-distance `threshold` plus the
 * matching relevance `score` (rounded, for display only).
 */
export const buildScoringTiers = (api, tierMeta) =>
	tierMeta.map(({ key, ...rest }) => {
		const threshold = getRecommendedThreshold(api, key);
		return {
			...rest,
			tier: key,
			threshold,
			score: Math.round(relevanceFromDistance(threshold, api)),
		};
	});

/** Domain relevance display value (rounded percentage). */
export const calculateRelevanceFromScore = (score) =>
	Math.round(relevanceFromDistance(score, "DOMAINS"));
